import { useCallback, useEffect } from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';

/**
 * ТЗ 0128 — ИИ выключен в настройках (`ai_disabled`). Флага доступности
 * сервер не отдаёт, поэтому ERP запоминает отказ с этим кодом: дальше кнопки
 * ИИ-писем (окно лида и договора) сразу выключены с подсказкой «ИИ выключен
 * в настройках», а не ждут второго отказа.
 *
 * Запомненное не вечно: ИИ могли включить в настройках, не перезагружая
 * вкладку. Флаг снимается
 *  - успешным черновиком (сервер проверяет `ai_disabled` раньше кеша —
 *    любой черновик значит «ИИ включён»);
 *  - через {@link SALES_AI_OFF_TTL_MS} после отказа;
 *  - когда вкладка снова в фокусе (вернулись из настроек) — следующий клик
 *    спросит сервер заново.
 */
export const SALES_AI_DISABLED_CODE = 'ai_disabled';

/** Сколько помнить отказ `ai_disabled`. */
export const SALES_AI_OFF_TTL_MS = 10 * 60 * 1000;

export const salesAiOffVar = makeVar(false);

let offTimer: ReturnType<typeof setTimeout> | null = null;

/** Забыть отказ: кнопки ИИ снова доступны. */
export const resetSalesAiOff = (): void => {
  if (offTimer) clearTimeout(offTimer);
  offTimer = null;
  if (salesAiOffVar()) salesAiOffVar(false);
};

/** Отказ генерации: `ai_disabled` — запомнить на {@link SALES_AI_OFF_TTL_MS}. */
export const noteSalesAiError = (code: string | null | undefined): void => {
  if (code !== SALES_AI_DISABLED_CODE) return;
  if (offTimer) clearTimeout(offTimer);
  offTimer = setTimeout(resetSalesAiOff, SALES_AI_OFF_TTL_MS);
  salesAiOffVar(true);
};

export const useSalesAiOff = () => {
  const off = useReactiveVar(salesAiOffVar);
  const note = useCallback(noteSalesAiError, []);
  useEffect(() => {
    if (!off) return undefined;
    window.addEventListener('focus', resetSalesAiOff);
    return () => window.removeEventListener('focus', resetSalesAiOff);
  }, [off]);
  return { off, note };
};
